import React from "react";
import SanitizedHTML from "components/formatting/SanitizedHTML";
import { useTheme, Box } from "@mui/material";

export interface EmbedData {
  link: string;
  site: string;
  title: string;
  handle: string;
}

interface EmbedProps {
  embed: EmbedData;
}

export default function Embed(props:EmbedProps): JSX.Element {
  const theme = useTheme();
  const embed = props.embed;

  // Differentiate YouTube embed types (video vs. shorts) based on URL structure
  const getYouTubeEmbed = (link: string) => {
    if (!link) return "";
    let src = "";
    let height = 315;
    if (link.includes("/shorts/")) {
      const shortsId = link.split("/shorts/")[1].split(/[?&]/)[0];
      src = `https://www.youtube.com/embed/${shortsId}`;
      height = 560;
    } else if (link.includes("watch?v=")) {
      const videoId = link.split("watch?v=")[1].split("&")[0];
      src = `https://www.youtube.com/embed/${videoId}`;
      height = 315;
    }
    if (!src) return "";
    return `<iframe width="100%" class="youtube-embed" height="${height}" src="${src}" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
  };

  // Ensure Reddit links are fully qualified https URLs
  const getRedditLink = (link:string) => {
    if (!/^https?:\/\//i.test(link)) {
      return `https://${link}`;
    }
    return link;
  };

  // Differentiate Reddit embed types (post vs. comment) based on URL structure
  const getRedditEmbed = (link: string) => {
    if (!link) return "";
    const url = getRedditLink(link);
    let height = 300;
    if (url.includes("/comments/") && !url.includes("/comment/")) {
      height = 240;
    } else if (url.includes("/comment/")) {
      height = 300;
    }
    return `<blockquote class="reddit-embed-bq" data-embed-height="${height}"><a href="${url}"></a></blockquote>`;
  };

  // Define embed elements for supported sites
  const embedElements: Record<string, string> = {
    "twitter.com": `<blockquote class="twitter-tweet"><a href="${embed.link}"></a></blockquote>`,
    "instagram.com": `<blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="${embed.link}"></blockquote>`,
    "tiktok.com": `<blockquote class="tiktok-embed" cite="${embed.link}" data-video-id="${embed.link.split("/").pop()}"><section></section></blockquote>`,
    "youtube.com": `${getYouTubeEmbed(embed.link)}`,
    "reddit.com": `${getRedditEmbed(embed.link)}`,
  };

  // Compute the appropriate embed element
  const embedElement = embedElements[embed.site] ?? "";

  // Custom styles for embed container per site if needed
  const embedStyles= {
    "iframe": {
      width: "100% !important",
      margin: "0px !important",
      padding: "0px !important"
    },
    ".twitter-tweet": { margin: "0px !important"  },
    ".tiktok-embed": { margin: "0px !important" },
    ".instagram-media": { borderRadius: "12px !important" },
    ".youtube-embed": { borderRadius: "12px !important", border: `1px solid ${theme.palette.divider}` },
  } 

  return (
    <Box sx={{ width: "100%", minHeight: "220px", m: 0, p: 0, ...embedStyles }}>
      <SanitizedHTML html={embedElement} style={{ display: "flex", justifyContent: "center" }}/>
    </Box>
  )
}
